import { Button, Input } from '@salecycle/ui-component-library';
import { Campaign } from 'entities/campaign';
import IP from 'entities/ip';
import { LifeCycle } from 'entities/lifecycle';
import { FC, useState } from 'react';
import { connect } from 'react-redux';
import { Selectors as Router } from 'router';
import { Actions as CampaignActions, Selectors as CampaignSelectors } from 'sections/campaigns/state/campaigns';
import { Selectors as GlobalIpsSelectors } from 'sections/campaigns/state/globalIpWhitelist';
import { Actions, Selectors } from 'sections/campaigns/state/ipWhitelist';
import { State } from 'store';
import styled from 'styled-components';
import IPWhitelistTable from './IPWhitelistTable';
import StateBadge from '../../components/StateBadge';
import { isValidIpAddress } from 'core/ui/authenticator/validIpAddressChecks';

interface IOwnProps {
  ipName?: string;
  ipAddress?: string;
}

interface IStateProps {
  siteId: string;
  campaignId: string;
  ips: { [id: string]: IP };
  globalIps: IP[];
  campaignLifecycle: LifeCycle;
  savedCampaignLifecycle: LifeCycle;
}

interface IDispatchProps {
  delete: (id: string) => void;
  create: (ip: IP) => void;
  updateCampaign: (campaignId: string, campaign: Campaign) => void;
}

export type IProps = IOwnProps & IStateProps & IDispatchProps;

const StyledNewIPContainer = styled.div`
  display: grid;
  grid-gap: 2rem;
  grid-template-columns: auto auto auto;
  align-items: flex-end;
  margin-bottom: 2rem;
`;

export const IPWhitelist: FC<IProps> = props => {
  const [name, setName] = useState(props.ipName || '');
  const [address, setAddress] = useState(props.ipAddress || '');
  const [nameError, setNameError] = useState(false);
  const [ipError, setIpError] = useState(false);

  const updateAddress = (value: string) => {
    setAddress(value);
    setIpError(!isValidIpAddress(value));
  };

  const submit = () => {
    const validName = name.length !== 0;
    const validIp = isValidIpAddress(address);

    !validName ? setNameError(true) : setNameError(false);

    !validIp ? setIpError(true) : setIpError(false);

    if (validName || validIp) {
      props.create({ ip: address, name: name });
    }
  };

  const testCampaign = () =>
    props.updateCampaign(props.campaignId, {
      state: 'TESTING',
    });

  const isLifecycleChanged =
    props.campaignLifecycle.state !== props.savedCampaignLifecycle.state ||
    props.campaignLifecycle.startTime !== props.savedCampaignLifecycle.startTime ||
    props.campaignLifecycle.endTime !== props.savedCampaignLifecycle.endTime;

  const messagePrefix = isLifecycleChanged
    ? 'If you save this campaign its status will change to'
    : 'This campaign is currently';

  return (
    <div>
      {props.campaignLifecycle.state === 'TESTING' ? (
        <div>
          <p>
            {messagePrefix} <StateBadge lifeCycle={props.campaignLifecycle} />
          </p>
          <p> RTMs {isLifecycleChanged ? ' will be ' : ' are '} restricted to the following IP addresses.</p>
          <StyledNewIPContainer>
            <Input
              ariaLabel="IP name"
              label="Name"
              spacingBelow={false}
              onChange={value => setName(value)}
              value={name}
              validationMessage={nameError && 'Name is required'}
            />
            <Input
              ariaLabel="IP address"
              label="IP Address"
              spacingBelow={false}
              onChange={updateAddress}
              value={address}
              validationMessage={ipError && 'IP address should be of the form 123.123.123.123'}
            />
            <div>
              <Button variation="secondary" onClick={submit}>
                Add IP
              </Button>
            </div>
          </StyledNewIPContainer>
          <IPWhitelistTable ips={props.ips} globalIps={props.globalIps} delete={props.delete} />
        </div>
      ) : (
        <div>
          <p>
            {messagePrefix} <StateBadge lifeCycle={props.campaignLifecycle} />
          </p>
          <p>
            There are no restrictions on who {isLifecycleChanged ? ' will be able to ' : ' can '} see messages. Add IP
            addresses to restrict who can see this campaign.
          </p>
          <Button variation="secondary" onClick={testCampaign}>
            Add IP addresses
          </Button>
        </div>
      )}
    </div>
  );
};

const mapState = (state: State): IStateProps => {
  const campaignId = Router.getCampaignId(state);
  const campaign = CampaignSelectors.getUpdatedEntity(state, campaignId);
  return {
    globalIps: GlobalIpsSelectors.getEntities(state),
    siteId: Router.getSiteId(state),
    campaignId,
    ips: Selectors.getEntityMap(state),
    campaignLifecycle: campaign,
    savedCampaignLifecycle: CampaignSelectors.getEntity(state, campaignId),
  };
};

const actions: IDispatchProps = {
  delete: Actions.requestDelete,
  create: (ip: IP) => Actions.requestCreate('NewWhitelistIP', ip),
  updateCampaign: CampaignActions.updateLocal,
};

export default connect(mapState, actions)(IPWhitelist);
